import { useEffect, useState, useContext } from "react";
import FormContainer from "components/form-container";
import ProgressBar from "components/progress-bar";
import { useLocation, useNavigate, createSearchParams } from "react-router-dom";
import { Input } from "components/input";
import { getQueryParams } from "lib/utils";
import AuthContext from "contexts/AuthContext";
import { isValidEmail } from "lib/utils";
import { useTranslation } from "react-i18next";
import InputValidation from "components/input-validation";
import { TAKEBACK_METHOD_NAME } from "consts/query-params";
import ForgotPasswordLink from "components/forgot-password-link";
import PasswordInput from "components/password-input";
import { useCreateTakeback } from "api/use-takebacks";
import LoadingSpinner from "components/loading-spinner";
import { calculateEstimate } from "lib/calculateEstimate";

export default function InStoreSignin() {
  const { t } = useTranslation();
  const [emailAlarm, setEmailAlarm] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = getQueryParams(location.search);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { loginHandler, loginError, user, authTokens } =
    useContext(AuthContext);

  const targetPath = t("urls.signup_store_confirmation");

  // Trade-in creation state
  const [shouldCreateTakeback, setShouldCreateTakeback] = useState(false);
  const { mutate: createTakeback, isLoading: isCreatingTakeback } =
    useCreateTakeback({
      onCreate: ({ sys_id }) => {
        // Pass the takebackId via query params if needed
        const navigateOptions = {
          pathname: targetPath,
          search: createSearchParams({
            ...queryParams,
            takebackId: sys_id,
          }).toString(),
        };
        navigate(navigateOptions);
      },
      onError: () => {
        // Optionally show an error message
      },
    });

  const giftRatio = calculateEstimate(1).giftRatio;

  useEffect(() => {
    if (shouldCreateTakeback && user && authTokens) {
      setShouldCreateTakeback(false);
      createTakeback({
        uom: t("scale.weight"),
        weight: queryParams.w ? parseFloat(queryParams.w) : 1,
        paymentType: queryParams.p,
        returnMethod: queryParams[TAKEBACK_METHOD_NAME],
        returnTypeOffered: queryParams.state || queryParams.country,
        store: queryParams[TAKEBACK_METHOD_NAME],
        originalReplayWeight: queryParams.replayWeight,
        incetiveRatio: giftRatio,
      });
    }
  }, [
    shouldCreateTakeback,
    user,
    authTokens,
    createTakeback,
    t,
    queryParams,
    giftRatio,
  ]);

  const submitHandler = () => {
    loginHandler(
      { username, password },
      {
        onSuccess: () => {
          setShouldCreateTakeback(true);
        },
      }
    );
  };

  if (shouldCreateTakeback || isCreatingTakeback) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <LoadingSpinner isLoading={"Creating Trade-In..."} />
      </div>
    );
  }

  return (
    <FormContainer
      submitLabel={t("ui.button_continue")}
      onSubmit={submitHandler}
      submitParams={queryParams}
      secondaryLabel={t("ui.button_back")}
      secondaryHref={t("urls.signup_store_email")}
      secondaryParams={queryParams}
      submitDisabled={!username || !password || emailAlarm}
    >
      <ProgressBar step={2} stepsNum={3} />
      <h2 className="font-semibold text-6xl max-w-lg">
        {t("ui.welcome_back")}
      </h2>
      <div className="w-full flex flex-col gap-x-large">
        <div>
          <Input
            label={t("ui.input_email")}
            type="email"
            id="username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setEmailAlarm(!isValidEmail(e.target.value));
            }}
          />
        </div>
        <div>
          <PasswordInput
            label={t("ui.input_password")}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="text-blue mt-regular text-sm cursor-pointer">
            <ForgotPasswordLink returnToPath={t("urls.signup_store_sign_in")} />
          </div>
          <InputValidation errorMessage={loginError?.message} />
        </div>
      </div>
    </FormContainer>
  );
}
